import React, { useEffect, useState } from "react";
import MainLayout from "layouts/Main";
import { Typography, Box, FormControl, Divider } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useAppDispatch, useAppSelector } from "hooks";
import Table from "components/Table";
import dayjs, { Dayjs } from "dayjs";
import { PeriodPresetTypes } from "helpers/enums";
import Select from "components/Select";
import {
  selectCurrentRoleClientId,
  selectReportsQueryOptions,
} from "store/selectors";
import { fetchReportsQueryOptions } from "store/appSlice";
import {
  buildClientReportRows,
  ClientReportRow,
} from "helpers/clientReportRows";
import {
  getClientReport,
  getClientReportExcelExport,
} from "api/resources/clients";
import { getClientGroupsByClientId } from "api/resources/clientGroups";
import { ClientGroup } from "api/models/ClientGroup";
import { ClientReport } from "api/models/Clients";
import downloadCSV from "helpers/excelExport";
import ClientSelect from "../Components/ClientSelect";
import { getTabsByRoleClientReports } from "helpers/tabs";

interface MonthRange {
  start: Dayjs | null;
  end: Dayjs | null;
}

const Summary = () => {
  const reportsQueryOptions = useAppSelector(selectReportsQueryOptions);
  const currentRoleClientId = useAppSelector(selectCurrentRoleClientId);
  const [snackbar, setSnackbar] = useState(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string | "">("");
  const [selectedClients, setSelectedClients] = useState<
    {
      title: string;
      value: number;
    }[]
  >([]);
  const [tableData, setTableData] = useState<ClientReportRow[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);
  const [selectedMonthByPeriod, setSelectedMonthByPeriod] =
    useState<MonthRange>({
      start: null,
      end: null,
    });
  const [clientReport, setClientReport] = useState<ClientReport>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [groupClients, setGroupClients] = useState<
    { title: string; value: number }[]
  >([]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchReportsQueryOptions());
  }, [dispatch]);

  useEffect(() => {
    if (!currentRoleClientId) {
      setGroupClients([]);
      return;
    }

    getClientGroupsByClientId(currentRoleClientId)
      .then((result) => {
        const groups: ClientGroup[] = result?.data ?? [];
        const clientsById = new Map(
          groups
            .flatMap((group) => group.clients)
            .map((client) => [client.id, client]),
        );
        setGroupClients(
          Array.from(clientsById.values()).map((c) => ({
            title: c.name,
            value: c.id,
          })),
        );
      })
      .catch(() => {
        setGroupClients([]);
      });
  }, [currentRoleClientId]);

  useEffect(() => {
    if (startDate && endDate && currentRoleClientId) {
      setLoading(true);

      const clientIds = selectedClients.map((sc) => {
        return sc.value;
      });

      getClientReport(
        currentRoleClientId,
        dayjs(startDate).format("YYYY-MM-DD"),
        dayjs(endDate).format("YYYY-MM-DD"),
        clientIds,
      )
        .then((result: any) => {
          setClientReport(result.data);
          setLoading(false);
        })
        .catch((error: any) => {
          console.error({ error });
          setLoading(false);
        });
    }
  }, [selectedClients, startDate, endDate, currentRoleClientId]);

  const snackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  const handlePeriodChange = (value: any) => {
    const newSelectedPeriod = value.props.value;

    if (newSelectedPeriod) {
      const periodStartDate = reportsQueryOptions.periodPresets.find(
        (preset) => preset.title === value.props.value,
      )?.period.from;

      const periodEndDate = reportsQueryOptions.periodPresets.find(
        (preset) => preset.title === value.props.value,
      )?.period.to;

      setStartDate(dayjs(periodStartDate));
      setEndDate(dayjs(periodEndDate));
      setSelectedPeriod(newSelectedPeriod);
    }
  };

  const handleExportDetailedReport = () => {
    setExportLoading(true);

    const fileName =
      selectedMonthByPeriod.start && selectedMonthByPeriod.end
        ? dayjs(selectedMonthByPeriod.start).format("DD.MM.YYYY") +
          "-" +
          dayjs(selectedMonthByPeriod.end).format("DD.MM.YYYY") +
          "_REPORT"
        : "REPORT";

    const clientIds = selectedClients.map((sc) => {
      return sc.value;
    });

    getClientReportExcelExport(
      currentRoleClientId,
      dayjs(selectedMonthByPeriod.start).format("YYYY-MM-DD"),
      dayjs(selectedMonthByPeriod.end).format("YYYY-MM-DD"),
      clientIds,
    )
      .then((result) => {
        setExportLoading(false);
        downloadCSV(result, fileName, true);
        setSnackbar({
          type: "success",
          message: "Report exported successfully",
        });
      })
      .catch((error: any) => {
        console.error({ error });
        setExportLoading(false);
        setSnackbar({
          type: "error",
          message: "Report export failed",
        });
      });
  };

  useEffect(() => {
    if (clientReport) {
      setTableData(buildClientReportRows(clientReport));
    }
  }, [clientReport]);

  const columns = [
    {
      accessorKey: "item",
      header:
        dayjs(startDate).format("DD.MM.YYYY") +
        " - " +
        dayjs(endDate).format("DD.MM.YYYY"),
    },
    {
      accessorKey: "value",
      header: "",
      meta: { isNumeric: true },
    },
  ];

  const handleMonthChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const startOfMonth = newValue.startOf("month");
      const endOfMonth = newValue.endOf("month");

      setSelectedMonthByPeriod({ start: startOfMonth, end: endOfMonth });
    }
    setSelectedMonth(newValue);
  };

  return (
    <MainLayout
      tabs={getTabsByRoleClientReports()}
      snackbar={{
        open: snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message,
      }}
    >
      <Typography variant="h5">Summary report</Typography>
      <Box maxWidth={1200}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginTop: 4, marginBottom: 2 }}
        >
          <Box>
            <Typography component="p" sx={{ mb: 2 }}>
              Summarized report
            </Typography>
            <FormControl>
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                {groupClients.length > 0 && (
                  <Box sx={{ mr: 1 }}>
                    <ClientSelect
                      selectedClients={selectedClients}
                      setSelectedClients={setSelectedClients}
                      clients={groupClients}
                    />
                  </Box>
                )}
                <Select
                  label="Period"
                  id="period"
                  name="period"
                  minWidth={180}
                  onChange={(event: any, value: any) =>
                    handlePeriodChange(value)
                  }
                  value={selectedPeriod}
                  options={
                    reportsQueryOptions
                      ? [...reportsQueryOptions.periodPresets].map(
                          (periodPreset) => {
                            return {
                              label:
                                PeriodPresetTypes[
                                  periodPreset.title as keyof typeof PeriodPresetTypes
                                ],
                              value: periodPreset.title,
                            };
                          },
                        )
                      : []
                  }
                />
                <DatePicker
                  sx={{ ml: 1, mr: 1, minWidth: "180px" }}
                  slotProps={{ textField: { size: "small" } }}
                  name="startDate"
                  value={startDate}
                  disableFuture={true}
                  onChange={(value) => {
                    setStartDate(dayjs(value));
                  }}
                  label="Start date"
                />
                <DatePicker
                  sx={{ mr: 1, minWidth: "180px" }}
                  slotProps={{ textField: { size: "small" } }}
                  name="endDate"
                  value={endDate}
                  disableFuture={true}
                  minDate={startDate}
                  onChange={(value) => {
                    setEndDate(dayjs(value));
                  }}
                  label="End date"
                />
              </Box>
            </FormControl>
          </Box>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ margin: "0 1.6rem" }}
          />
          <Box>
            <Typography component="p" sx={{ mb: 2 }}>
              Export detailed report
            </Typography>
            <FormControl>
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                <DatePicker
                  sx={{ mr: 1, maxWidth: "180px" }}
                  slotProps={{ textField: { size: "small" } }}
                  name="period"
                  value={selectedMonth}
                  disableFuture={true}
                  minDate={dayjs().subtract(2, "year")}
                  openTo="month"
                  views={["year", "month"]}
                  onChange={handleMonthChange}
                  label="Period"
                />
                <LoadingButton
                  variant="contained"
                  disabled={!selectedMonth || exportLoading}
                  loading={exportLoading}
                  loadingIndicator="Loading..."
                  sx={{ ml: 1 }}
                  onClick={handleExportDetailedReport}
                >
                  Export
                </LoadingButton>
              </Box>
            </FormControl>
          </Box>
        </Box>
        {startDate && endDate && (
          <Table
            isFetching={loading}
            data={tableData}
            columns={columns}
            pageCount={1}
          />
        )}
      </Box>
    </MainLayout>
  );
};

export default Summary;
